import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PortalLayout from "@/pages/portal/PortalLayout";
import api from "@/lib/api";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ProjectTimeline from "@/components/ProjectTimeline";
import ArtworkPanel from "@/components/ArtworkPanel";
import { CONSULTATION_URL } from "@/data/constants";

export const PortalProjects = () => {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/portal/projects").then((r) => setProjects(r.data.projects)).catch(() => {});
  }, []);

  return (
    <PortalLayout>
      <div className="mb-8">
        <div className="ft-eyebrow mb-2">Projects</div>
        <h1 className="text-3xl font-bold tracking-tight">Your beverage launches.</h1>
      </div>

      {projects.length === 0 ? (
        <div className="ft-card text-center py-16">
          <p className="text-[var(--ft-muted)] mb-4">No projects yet. Book a consultation to get started.</p>
          <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-primary inline-flex">Book Consultation</a>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="portal-projects-grid">
          {projects.map((p) => (
            <Link key={p.id} to={`/portal/projects/${p.id}`} className="ft-card block" data-testid={`portal-project-card-${p.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="ft-badge">{p.stage}</span>
                {p.is_reorder && <span className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Reorder</span>}
              </div>
              <h2 className="mt-4 text-xl font-semibold">{p.name}</h2>
              <p className="text-sm text-[var(--ft-muted)] mt-1">{p.product_category}</p>
              <div className="mt-5">
                <div className="flex justify-between text-xs text-[var(--ft-muted)] mb-2">
                  <span>Progress</span>
                  <span>{p.progress}%</span>
                </div>
                <div className="w-full h-1.5 bg-[var(--ft-border)] rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${p.progress}%`, background: "var(--ft-blue)" }} />
                </div>
              </div>
              {p.target_launch && (
                <div className="mt-5 pt-4 border-t border-[var(--ft-border)] text-xs text-[var(--ft-muted)]">
                  Target launch: <span className="font-semibold text-[var(--ft-charcoal)]">{p.target_launch}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </PortalLayout>
  );
};

export const PortalProjectDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/portal/projects/${id}`).then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post("/portal/messages", { project_id: id, body: msg });
      setMsg("");
      await load();
    } catch (err) {
      toast.error("Failed to send message");
    } finally { setSending(false); }
  };

  if (!data) return <PortalLayout><div>Loading…</div></PortalLayout>;
  const { project, messages } = data;

  return (
    <PortalLayout>
      <Link to="/portal/projects" className="ft-btn-ghost inline-flex items-center gap-2 -ml-4 mb-4" data-testid="portal-back">
        <ArrowLeft size={16} /> Projects
      </Link>
      <div className="mb-8 flex items-center gap-3 flex-wrap">
        <span className="ft-badge">{project.stage}</span>
        {project.is_reorder && <span className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Reorder</span>}
      </div>
      <h1 className="text-3xl font-bold tracking-tight">{project.name}</h1>
      <p className="text-[var(--ft-muted)] mt-1 mb-8">{project.product_category}</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="ft-card">
            <div className="ft-eyebrow mb-3">Progress</div>
            <div className="flex justify-between text-sm mb-2">
              <span>{project.stage}</span>
              <span className="font-semibold">{project.progress}%</span>
            </div>
            <div className="w-full h-2 bg-[var(--ft-border)] rounded-full overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${project.progress}%`, background: "var(--ft-blue)" }} />
            </div>
            {project.notes && <p className="mt-5 text-sm text-[var(--ft-muted)] leading-relaxed">{project.notes}</p>}
          </div>

          <ArtworkPanel projectId={project.id} />

          <div className="ft-card">
            <div className="ft-eyebrow mb-4">Project Messages</div>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2 mb-5" data-testid="portal-messages-list">
              {messages.length === 0 && <p className="text-sm text-[var(--ft-muted)]">No messages yet. Start the conversation with your account team.</p>}
              {messages.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] p-3 rounded-xl ${mine ? "bg-[var(--ft-blue)] text-white" : "bg-[var(--ft-soft-gray)] text-[var(--ft-charcoal)]"}`}>
                      <div className="text-xs opacity-70 mb-1">{m.sender_name} · {m.sender_role}</div>
                      <div className="text-sm">{m.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="flex gap-2" data-testid="portal-message-form">
              <input
                className="ft-input flex-1"
                placeholder="Message your account team…"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                data-testid="portal-message-input"
              />
              <button type="submit" disabled={sending} className="ft-btn-primary" data-testid="portal-message-send">
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>

        <aside className="space-y-6">
          <ProjectTimeline stage={project.stage} updatedAt={project.updated_at} />

          <div className="ft-card-flat">
            <div className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Category</div>
            <div className="font-semibold mt-1">{project.product_category}</div>
          </div>
          {project.target_launch && (
            <div className="ft-card-flat">
              <div className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Target Launch</div>
              <div className="font-semibold mt-1">{project.target_launch}</div>
            </div>
          )}
          <div className="ft-card-flat">
            <div className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Project ID</div>
            <div className="font-mono text-xs mt-1 truncate">{project.id}</div>
          </div>
        </aside>
      </div>
    </PortalLayout>
  );
};
